/**
 * TerrainPalette - 地形選択パレットコンポーネント
 *
 * マップエディタで地形タイプを選択するためのパレット。
 */

import type { EditorTerrainType } from './HexGrid';

// ============================================================================
// 型定義
// ============================================================================

export type EditorTool = 'terrain' | 'city' | 'town' | 'eraser' | 'expand' | 'ferryLink' | 'adjacentLink' | 'wall' | 'preLaidTrack';

export interface TerrainPaletteProps {
  selectedTool: EditorTool;
  selectedTerrain: EditorTerrainType;
  onToolChange: (tool: EditorTool) => void;
  onTerrainChange: (terrain: EditorTerrainType) => void;
}

// ============================================================================
// 定数
// ============================================================================

const TERRAIN_OPTIONS: { type: EditorTerrainType; label: string; color: string }[] = [
  { type: 'plains', label: 'Plains', color: 'hsl(var(--terrain-grass))' },
  { type: 'mountains', label: 'Mountains', color: 'hsl(var(--terrain-mountain))' },
  { type: 'river', label: 'River', color: 'hsl(var(--terrain-water))' },
  { type: 'water', label: 'Water', color: 'hsl(210, 80%, 40%)' },
];

const TOOL_OPTIONS: { tool: EditorTool; label: string; icon: string }[] = [
  { tool: 'terrain', label: 'Terrain', icon: '🌍' },
  { tool: 'city', label: 'City', icon: '🏙️' },
  { tool: 'town', label: 'Town', icon: '🏘️' },
  { tool: 'eraser', label: 'Eraser', icon: '🧹' },
  { tool: 'expand', label: 'Expand', icon: '➕' },
  { tool: 'ferryLink', label: 'Ferry', icon: '⛴️' },
  { tool: 'adjacentLink', label: 'Adjacent', icon: '🔗' },
  { tool: 'wall', label: 'Wall', icon: '🧱' },
  { tool: 'preLaidTrack', label: 'Pre-Track', icon: '🛤️' },
];

// ============================================================================
// TerrainPalette コンポーネント
// ============================================================================

export function TerrainPalette({
  selectedTool,
  selectedTerrain,
  onToolChange,
  onTerrainChange,
}: TerrainPaletteProps) {
  return (
    <div className="flex flex-col gap-4 p-4 bg-card border border-border rounded-lg">
      {/* ツール選択 */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Tool</h3>
        <div className="grid grid-cols-2 gap-2">
          {TOOL_OPTIONS.map(({ tool, label, icon }) => (
            <button
              key={tool}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-md text-sm
                transition-all duration-150
                ${selectedTool === tool
                  ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
                }
              `}
              onClick={() => onToolChange(tool)}
            >
              <span>{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 地形選択（Terrain/Expandツール選択時のみ表示） */}
      {(selectedTool === 'terrain' || selectedTool === 'expand') && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Terrain Type</h3>
          <div className="grid grid-cols-2 gap-2">
            {TERRAIN_OPTIONS.map(({ type, label, color }) => (
              <button
                key={type}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm
                  transition-all duration-150
                  ${selectedTerrain === type
                    ? 'ring-2 ring-primary/50 ring-offset-1'
                    : 'hover:opacity-80'
                  }
                `}
                style={{ backgroundColor: color }}
                onClick={() => onTerrainChange(type)}
              >
                <span className="w-4 h-4 rounded-sm border border-white/30" style={{ backgroundColor: color }} />
                <span className="text-white font-medium drop-shadow-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* City/Townツールのヒント */}
      {selectedTool === 'city' && (
        <div className="text-xs text-muted-foreground">
          Click on a hex to add a city. Right-click to edit.
        </div>
      )}
      {selectedTool === 'town' && (
        <div className="text-xs text-muted-foreground">
          Click on a hex to add a town. Right-click to edit.
        </div>
      )}
      {selectedTool === 'eraser' && (
        <div className="text-xs text-muted-foreground">
          Click on a hex to remove city/town and reset to plains.
        </div>
      )}
      {selectedTool === 'expand' && (
        <div className="text-xs text-muted-foreground">
          Click on a hex to add adjacent hexes with selected terrain.
        </div>
      )}
      {selectedTool === 'ferryLink' && (
        <div className="text-xs text-muted-foreground">
          Click two hexes to create a ferry link between them (curved line).
        </div>
      )}
      {selectedTool === 'adjacentLink' && (
        <div className="text-xs text-muted-foreground">
          Click two adjacent hexes to create a link between them (circle marker).
        </div>
      )}
      {selectedTool === 'wall' && (
        <div className="text-xs text-muted-foreground">
          Click a hex to toggle walls. Select edge direction in popup.
        </div>
      )}
      {selectedTool === 'preLaidTrack' && (
        <div className="text-xs text-muted-foreground">
          Click a hex to add pre-laid track (owner: none).
        </div>
      )}
    </div>
  );
}

export default TerrainPalette;
